import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, usd } from '../lib/api';
import { ErrorNote, useLoad } from '../components/ui';

export default function Invoices() {
  const nav = useNavigate();
  const list = useLoad(() => api<any[]>('/api/invoices'), []);
  const pos = useLoad(() => api<any[]>('/api/purchase-orders?status=sent'), []);
  const [poId, setPoId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // no photo: an empty invoice to type in
  async function startBlank() {
    setBusy(true); setErr(null);
    try { const inv = await api<any>('/api/invoices/new', { body: { poId: poId || null } }); nav(`/invoices/${inv.id}`); }
    catch (e) { setErr(e); setBusy(false); }
  }

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true); setErr(null);
    const form = new FormData();
    if (poId) form.append('poId', poId);
    for (const f of Array.from(files)) form.append('file', f, f.name);
    try { const inv = await api<any>('/api/invoices', { form }); nav(`/invoices/${inv.id}`); }
    catch (e) { setErr(e); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  }

  return (
    <div className="stack">
      <div className="page-head"><div className="grow"><h1>Invoices</h1><p>Photograph each vendor invoice when it arrives. Lines are read, matched to your items and checked against the PO and the posted CPL price. Nothing changes stock or bills until you post it.</p></div></div>
      <div className="panel">
        <div className="panel-body stack-s">
          <div className="row wrap">
            <label className="grow" style={{ maxWidth: 360 }}>Delivery for purchase order (optional)
              <select value={poId} onChange={(e) => setPoId(e.target.value)}>
                <option value="">No PO</option>
                {pos.data?.map((p) => <option key={p.id} value={p.id}>{p.po_number} · {p.wholesaler_name}</option>)}
              </select>
            </label>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" multiple hidden onChange={(e) => upload(e.target.files)} />
            <button className="primary big" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? 'Reading invoice…' : 'Take photo or upload'}</button>
            <button className="big" disabled={busy} onClick={startBlank}>New invoice</button>
          </div>
          <p className="faint">Several pages? Select all the photos at once. PDFs from a vendor portal work too. No photo? Press New invoice and type it in.</p>
          <ErrorNote error={err} />
        </div>
      </div>
      <ErrorNote error={list.error} />
      <div className="panel">
        {list.data?.length === 0 ? <div className="empty">No invoices yet.</div> : (
          <table className="grid">
            <thead><tr><th>Vendor</th><th>Invoice</th><th>Delivered</th><th>Status</th><th className="n">Lines to check</th><th className="n">Total</th></tr></thead>
            <tbody>{list.data?.map((i) => (
              <tr key={i.id} className="clickable" onClick={() => nav(`/invoices/${i.id}`)}>
                <td>{i.wholesaler_name ?? <span className="chip bad">Unknown</span>}</td><td>{i.invoice_no ?? '—'}</td><td>{i.delivery_date ?? '—'}</td>
                <td><span className={`chip ${i.status === 'posted' ? 'good' : 'warn'}`}>{i.status === 'needs_review' ? 'To review' : i.status}</span></td>
                <td className="n">{i.status === 'posted' ? '' : i.flagged_lines}</td><td className="n">{usd(i.total_cents)}</td>
              </tr>))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
